import { useNavigate } from 'react-router';
import type { platform } from '@platform/contracts';
import {
  Alert,
  Badge,
  Box,
  Button,
  CreditCardIcon,
  DataTable,
  EmptyState,
  MONO,
  Page,
  PageHeader,
  rem,
  Skeleton,
  Stack,
  TABULAR,
  Typography,
  type Column,
} from '@platform/design-system';
import { RouterLink, describeError, useCanOpen } from '@platform/app-kit';
import { useMeteringUsage, usePackages, useTenants } from '../api/queries.ts';
import {
  assistantTotals,
  dollars,
  formatNumber,
  MODEL_LABEL,
  spendState,
  utcPeriods,
  type AssistantTotals,
} from '../shell/format.ts';
import { PACKAGE_WRITE_ROUTE } from '../shell/routes.ts';
import { SpendBar } from '../shell/spend-bar.tsx';

interface Row {
  readonly tenant: platform.Tenant;
  readonly allowance?: platform.AssistantAllowance;
  readonly month: AssistantTotals;
  readonly today: AssistantTotals;
}

/** On / Not set up / Near the limit / Stopped, in words (DS-COST-04). */
export function AssistantStateBadge({
  allowance,
  month,
  today,
}: {
  allowance: platform.AssistantAllowance | undefined;
  month: AssistantTotals;
  today: AssistantTotals;
}) {
  if (!allowance) return <Badge label="Not set up" />;
  const states = [
    spendState(month.spentMicroUsd, allowance.monthlyLimitMicroUsd),
    spendState(today.spentMicroUsd, allowance.dailyLimitMicroUsd),
  ];
  if (states.includes('stopped')) return <Badge tone="error" label="Stopped at a limit" />;
  if (states.includes('warning')) return <Badge tone="warning" label="Near a limit" />;
  return <Badge tone="success" label="On" />;
}

/**
 * `/usage` — what the assistant (AI model calls) costs, per tenant, at first glance (design
 * standard DS-COST): this month and today as "$spent of $limit" bars with the count of calls, the
 * state in words, the tokens and the plan's limits. The numbers are the control plane's metering
 * (every call is reported by the cell that ran it); the limits are each tenant's package snapshot,
 * which the cell enforces before every call.
 */
export function UsagePage() {
  const periods = utcPeriods();
  const tenants = useTenants();
  const packages = usePackages(true);
  const month = useMeteringUsage(periods.month);
  const today = useMeteringUsage(periods.day);
  const navigate = useNavigate();
  const canEditLimits = useCanOpen(PACKAGE_WRITE_ROUTE);
  const packageName = (id: string) => packages.data?.find((p) => p.packageId === id)?.name ?? id;

  const rows: Row[] = (tenants.data ?? [])
    .map((tenant) => {
      const allowance = tenant.entitlement?.assistant;
      return {
        tenant,
        ...(allowance ? { allowance } : {}),
        month: assistantTotals(month.data?.find((u) => u.tenantId === tenant.tenantId)),
        today: assistantTotals(today.data?.find((u) => u.tenantId === tenant.tenantId)),
      };
    })
    .filter((r) => r.allowance !== undefined || r.month.calls > 0);
  const sum = (pick: (r: Row) => number) => rows.reduce((n, r) => n + pick(r), 0);
  const monthSpent = sum((r) => r.month.spentMicroUsd);
  const todaySpent = sum((r) => r.today.spentMicroUsd);
  const stopped = rows.filter(
    (r) =>
      r.allowance &&
      (spendState(r.month.spentMicroUsd, r.allowance.monthlyLimitMicroUsd) === 'stopped' ||
        spendState(r.today.spentMicroUsd, r.allowance.dailyLimitMicroUsd) === 'stopped'),
  ).length;

  const columns: Column<Row>[] = [
    {
      id: 'tenant',
      header: 'Tenant',
      sortValue: (r) => r.tenant.name,
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {r.tenant.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.tertiary"
            sx={{ ...MONO, display: 'block' }}
            noWrap
          >
            {r.tenant.slug}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'state',
      header: 'Assistant',
      cell: (r) => <AssistantStateBadge allowance={r.allowance} month={r.month} today={r.today} />,
    },
    {
      id: 'month',
      header: 'This month',
      sortValue: (r) => r.month.spentMicroUsd,
      cell: (r) => (
        <SpendBar
          label={periods.month}
          period="this month's"
          resetsEvery="month"
          spentMicroUsd={r.month.spentMicroUsd}
          limitMicroUsd={r.allowance?.monthlyLimitMicroUsd}
          calls={r.month.calls}
        />
      ),
    },
    {
      id: 'today',
      header: 'Today',
      sortValue: (r) => r.today.spentMicroUsd,
      cell: (r) => (
        <SpendBar
          label={periods.day}
          period="today's"
          resetsEvery="day"
          spentMicroUsd={r.today.spentMicroUsd}
          limitMicroUsd={r.allowance?.dailyLimitMicroUsd}
          calls={r.today.calls}
        />
      ),
    },
    {
      id: 'tokens',
      header: 'Tokens this month',
      numeric: true,
      sortValue: (r) => r.month.inputTokens + r.month.outputTokens,
      cell: (r) => (
        <Box component="span" sx={TABULAR}>
          {formatNumber(r.month.inputTokens)} in · {formatNumber(r.month.outputTokens)} out
        </Box>
      ),
    },
    {
      id: 'plan',
      header: 'Plan',
      cell: (r) =>
        r.allowance && r.tenant.entitlement ? (
          <Stack spacing={0.25}>
            <Typography variant="body2">
              {packageName(r.tenant.entitlement.packageId)} · v{r.tenant.entitlement.packageVersion}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {r.allowance.models.map((m) => MODEL_LABEL[m]).join(', ')}
              {r.allowance.memberMonthlyLimitMicroUsd !== undefined
                ? ` · ${dollars(r.allowance.memberMonthlyLimitMicroUsd)} a person a month`
                : ''}
            </Typography>
          </Stack>
        ) : (
          'Not in the plan'
        ),
    },
  ];

  const failed = [tenants, month, today].find((q) => q.isError);
  const loading = tenants.isLoading || month.isLoading || today.isLoading;
  const kpis: Kpi[] = [
    {
      label: 'This month',
      value: dollars(monthSpent),
      caption: `${formatNumber(sum((r) => r.month.calls))} calls across ${rows.length} ${rows.length === 1 ? 'tenant' : 'tenants'}`,
    },
    {
      label: 'Today',
      value: dollars(todaySpent),
      caption: `${formatNumber(sum((r) => r.today.calls))} calls`,
    },
    {
      label: 'Stopped at a limit',
      value: String(stopped),
      caption:
        stopped === 0
          ? 'No tenant is at a limit'
          : `${stopped === 1 ? 'Its' : 'Their'} calls are refused until the period ends or the limit rises`,
    },
  ];
  return (
    <Page>
      <PageHeader
        title="Assistant usage"
        meta={`${periods.month} (UTC month) and ${periods.day} (UTC day), the periods the provider bills by`}
        actions={
          canEditLimits ? (
            <Button component={RouterLink} href="/packages" variant="outlined">
              Change limits
            </Button>
          ) : undefined
        }
      />
      {failed ? (
        // Zeros would read as "nothing spent": show no figures rather than wrong ones.
        <Alert severity="error">
          {describeError(failed.error, 'Could not load the usage. Reload the page to try again.')}
        </Alert>
      ) : (
        <>
          <KpiStrip items={kpis} loading={loading} />
          <DataTable
            label="Assistant usage"
            columns={columns}
            rows={rows}
            getRowId={(r) => r.tenant.tenantId}
            loading={loading}
            defaultSort={{ columnId: 'month', direction: 'desc' }}
            onRowClick={(r) => void navigate(`/tenants/${r.tenant.tenantId}`)}
            emptyState={
              <EmptyState
                plain
                icon={<CreditCardIcon />}
                title="No tenant has the assistant"
                description="Add assistant limits to a package; tenants get them when it is applied."
                action={
                  canEditLimits ? (
                    <Button component={RouterLink} href="/packages" variant="contained">
                      Open packages
                    </Button>
                  ) : null
                }
              />
            }
          />
        </>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        Limits come from each tenant&apos;s package snapshot and are checked before every call: a
        call that could cross one is refused and costs nothing. Callers are warned from 80%. Spend
        is the cost of finished calls as the cells report them.
      </Typography>
    </Page>
  );
}

interface Kpi {
  readonly label: string;
  readonly value: string;
  readonly caption: string;
}

/**
 * One card, cells separated by hairlines: label 12.5/500 text-2, value in the display style with
 * tabular figures, caption 12 text-3 (SDL `KpiStrip`). Skeleton cells while loading.
 */
function KpiStrip({ items, loading }: { items: readonly Kpi[]; loading: boolean }) {
  return (
    <Box
      component="section"
      aria-label="Totals"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: `repeat(${items.length}, 1fr)` },
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        mb: 2.5,
        '& > *:not(:first-of-type)': {
          borderTop: { xs: 1, md: 0 },
          borderLeft: { xs: 0, md: 1 },
          borderColor: 'divider',
        },
      }}
    >
      {items.map((k) => (
        <Stack key={k.label} spacing={0.5} sx={{ p: 2, minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: rem(12.5), fontWeight: 500 }}
          >
            {k.label}
          </Typography>
          {loading ? (
            <>
              <Skeleton variant="text" width="45%" sx={{ fontSize: '1.75rem' }} />
              <Skeleton variant="text" width="70%" />
            </>
          ) : (
            <>
              <Typography variant="h1" component="p" sx={TABULAR}>
                {k.value}
              </Typography>
              <Typography variant="caption" color="text.tertiary" sx={TABULAR}>
                {k.caption}
              </Typography>
            </>
          )}
        </Stack>
      ))}
    </Box>
  );
}
